import {
  Component,
  inject,
  AfterViewInit,
  signal,
  computed,
  input,
  output,
  OnInit,
  OnDestroy,
  ElementRef,
  viewChild,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DifyGeneralService } from '../dify-general.service';
import { DifyVoiceReportService, ReportType } from '../dify-voice-report.service';
import { MockReportService, VoiceAttachment } from '@/app/services/mock-report.service';
import { QuickActionStateService } from '@/app/services/quick-action-state.service';
import { AuthService } from '@/app/core/services/auth.service';
import { ChatMessageComponent } from '../chat-message/chat-message.component';
import {
  ReportConfirmCardComponent,
} from '../report-confirm-card/report-confirm-card.component';
import { getConfirmFieldDefs } from '../report-field-labels';
import { AssistantMode } from '../types';
import { AudioService } from '@/app/services/audio.service';
import { Router } from '@angular/router';
import { PathUnit, getPath } from '@/app/consts/path.const';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-chat-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ChatMessageComponent,
    ReportConfirmCardComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './chat-dialog.component.html',
  styleUrl: './chat-dialog.component.scss',
})
export class ChatDialogComponent implements OnInit, AfterViewInit {
  private readonly generalService = inject(DifyGeneralService);
  private readonly voiceReportService = inject(DifyVoiceReportService);
  private readonly mockReportService = inject(MockReportService);
  private readonly quickActionState = inject(QuickActionStateService);
  private readonly authService = inject(AuthService);
  private readonly audioService = inject(AudioService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  /** 便捷取翻译 */
  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  private readonly textareaRef = viewChild<ElementRef<HTMLTextAreaElement>>('msgInput');
  private readonly messagesContainerRef = viewChild<ElementRef<HTMLDivElement>>('messagesContainer');
  private mutationObserver?: MutationObserver;

  /** 语音相关状态 */
  voiceSupported = this.audioService.supported;
  isRecording = this.audioService.isRecording;
  isTranscribing = this.audioService.isTranscribing;
  transcribeError = this.audioService.transcribeError;

  /** 外部指定初始模式（FAB 点击功能按钮时传入） */
  initialMode = input<AssistantMode>('general');
  /** 请求关闭事件（供外部 FAB 收起对话框） */
  closeRequest = output<void>();

  /** 当前模式：通用助手 / 语音报报告 */
  mode = signal<'general' | 'voice-report'>('general');

  /** 语音报报告流程状态（来自 service，组件销毁后仍保留） */
  voiceStep = this.voiceReportService.voiceStep;
  reportType = this.voiceReportService.reportType;
  confirmFields = this.voiceReportService.confirmFields;
  /** 是否可进入语音报报告（已 check-in） */
  canVoiceReport = computed(() => !!this.quickActionState.checkInState()?.vehicle);

  // 通用助手状态
  generalMessages = this.generalService.messages;
  generalIsStreaming = this.generalService.isStreaming;
  generalConnectionStatus = this.generalService.connectionStatus;
  generalParameters = this.generalService.parameters;
  generalOpeningStatement = this.generalService.processedOpeningStatement;

  // 语音报报告状态
  voiceMessages = this.voiceReportService.messages;
  voiceIsStreaming = this.voiceReportService.isStreaming;
  voiceConnectionStatus = this.voiceReportService.connectionStatus;
  restoredFromDraft = this.voiceReportService.restoredFromDraft;

  inputText = '';

  /** 当前生效的消息/流式/连接状态（按模式切换） */
  messages = computed(() =>
    this.mode() === 'general' ? this.generalMessages() : this.voiceMessages()
  );
  isStreaming = computed(() =>
    this.mode() === 'general' ? this.generalIsStreaming() : this.voiceIsStreaming()
  );
  connectionStatus = computed(() =>
    this.mode() === 'general' ? this.generalConnectionStatus() : this.voiceConnectionStatus()
  );

  /** 是否显示输入区：语音报报告在"选择类型"创建页、以及提交完成后不显示 */
  showInputArea = computed(
    () => this.mode() !== 'voice-report'
      || (this.voiceStep() !== 'select-type' && this.voiceStep() !== 'completed')
  );

  ngOnInit(): void {
    // 应用外部传入的初始模式，并按会话闭环规则进入对应入口
    this.switchMode(this.initialMode() === 'voice-report' ? 'voice-report' : 'general');
  }

  ngAfterViewInit(): void {
    this.scrollToBottom();
    // 监听消息容器内容变化（新消息、流式打字、确认卡片出现等）自动滚动到底部，
    // 用户无需手动下拉即可始终看到最新内容。
    const container = this.messagesContainerRef()?.nativeElement;
    if (container) {
      this.mutationObserver = new MutationObserver(() => this.scrollToBottom());
      this.mutationObserver.observe(container, { childList: true, subtree: true, characterData: true });
    }
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
  }

  // ================= 模式切换（会话闭环） =================

  protected switchMode(mode: 'general' | 'voice-report'): void {
    if (this.mode() === mode) return;
    this.mode.set(mode);
    this.inputText = '';

    // 闭环规则：切换到"创建差异报告"时
    // - 无进行中报告（voiceStep === 'idle'）→ 进入创建页 select-type
    // - 有进行中报告（chatting/confirm）→ 保留，恢复之前的对话进度
    if (mode === 'voice-report' && this.voiceStep() === 'idle') {
      this.voiceReportService.setVoiceStep('select-type');
    }
    this.scrollToBottom();
  }

  /** 关闭"已恢复未提交报告"的提示条 */
  protected dismissRestored(): void {
    this.voiceReportService.restoredFromDraft.set(false);
  }

  /**
   * 开始一份新的差异报告（用户主动点击"新建"）。
   * 立即重置到创建页。
   */
  protected startNewReport(): void {
    this.voiceReportService.startNewReport();
    this.inputText = '';
    this.scrollToBottom();
  }

  /** 打开完整的"创建差异报告"页面（悬浮球聊天的外链入口） */
  protected openFullPage(): void {
    this.closeRequest.emit();
    this.router.navigate([getPath(PathUnit.DeviationReportStudio)]);
  }

  // ================= 语音报报告：类型选择 =================

  protected selectReportType(type: ReportType): void {
    if (!this.canVoiceReport()) return;
    // 选择类型即开始一份新报告：设置类型、预填字段、开启新会话、进入采集中
    this.voiceReportService.startReportOfType(type);
    this.scrollToBottom();
  }

  protected selectReportTypeOrHint(type: ReportType): void {
    if (!this.canVoiceReport()) return;
    this.selectReportType(type);
  }

  // ================= 语音输入（点击录制，非按住） =================

  /**
   * 语音按钮：点击开始 / 再次点击结束录制。
   * 一次点击录一整段，结束录音后自动转文字并填入输入框。
   */
  protected toggleRecording(): void {
    if (this.isTranscribing() || this.isStreaming()) return;
    if (this.isRecording()) {
      this.stopAndTranscribe();
    } else {
      this.audioService.startRecording().catch(err => {
        this.transcribeError.set(err.message || this.t('chatDialog.startRecordingError'));
      });
    }
  }

  /** 停止录音 → 转 mp3 → STT 转文字 → 填入输入框（供用户编辑后发送） */
  private stopAndTranscribe(): void {
    this.audioService.stopRecording().then(async (blob) => {
      if (!blob || !blob.size) return;
      const text = await this.audioService.transcribe(blob);
      if (text) {
        this.inputText = (this.inputText ? this.inputText + ' ' : '') + text;
      }
      this.scrollToBottom();
    });
  }

  /** 语音报报告补问时自动播报（TTS） */
  protected speak(text: string): void {
    if (this.mode() === 'voice-report' && text) {
      this.audioService.speak(text);
    }
  }

  // ================= 输入框自动增高 =================

  /** 输入框随内容增高，最高限制（Pad 大屏可稍高） */
  protected onInputResize(): void {
    const el = this.textareaRef()?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }

  // ================= 发送 =================

  protected send(): void {
    const text = this.inputText.trim();
    if (!text || this.isStreaming()) return;
    this.inputText = '';
    this.resetTextareaHeight();

    if (this.mode() === 'general') {
      this.generalService.sendMessage(text, {
        onChunk: () => this.scrollToBottom(),
        onDone: () => this.scrollToBottom(),
        onError: () => this.scrollToBottom(),
      });
    } else {
      // 语音报报告：onDone 后识别 Dify 的"采集完成"标记，有标记才拉变量进确认
      this.voiceReportService.sendMessage(text, {
        onChunk: () => this.scrollToBottom(),
        onDone: async (conversationId) => {
          this.scrollToBottom();
          // 从最后一条 assistant 消息中识别确认标记
          const lastAssistant = [...this.voiceMessages()].reverse().find(m => m.role === 'assistant');
          const hasMarker = !!lastAssistant &&
            ChatDialogComponent.CONFIRM_MARKER.test(lastAssistant.content);
          if (!hasMarker) {
            // 无标记 → 采集未完成，停留在聊天继续补问，并语音播报补问话术
            if (lastAssistant) this.speak(lastAssistant.content);
            return;
          }
          // 有标记 → 剥离标记（不污染用户可见消息），拉取变量展示确认表单
          if (lastAssistant) {
            const cleaned = this.stripConfirmMarker(lastAssistant.content);
            this.voiceMessages.update(msgs =>
              msgs.map(m => (m.id === lastAssistant.id ? { ...m, content: cleaned } : m))
            );
          }
          await this.collectAndShowConfirm(conversationId);
        },
        onError: () => this.scrollToBottom(),
      });
    }
  }

  /** 剥离消息内容中的确认标记，避免展示给用户 */
  private stripConfirmMarker(content: string): string {
    return content.replace(ChatDialogComponent.CONFIRM_MARKER, '').trim();
  }

  /**
   * Dify "回复确认"节点（answer_result，仅采集完成的 ready 分支执行）在回复末尾
   * 附带的机器标记。前端识别到该标记即认为采集已完成，去拉取会话变量并展示确认表单。
   * 补问分支（answer_followup）不带此标记，前端不会触发确认流程。
   *
   * 用宽松正则匹配（兼容本地 `【__SHOW_CONFIRM__】` 与公司旧 DSL `【SHOW_CONFIRM】`）。
   */
  private static readonly CONFIRM_MARKER = /【_*SHOW_CONFIRM_*】/g;

  /**
   * 采集结束后拉取会话结构化字段，组装确认卡片。
   *
   * 由 Dify 端判定采集是否完成：只有 `ready=true`（所有语音必填字段都采集到）时，
   * "回复确认"节点才会输出 `CONFIRM_MARKER` 标记。前端在 `onDone` 时识别标记后
   * 调用此方法，拉取会话变量 `collected_fields`（含 `eventDescription`），
   * 组装确认卡片并进入 confirm 阶段。
   */
  /** 由系统自动补全日期的时间字段（需在确认卡片提醒用户） */
  protected timeAutoComplected: string[] = [];

  private async collectAndShowConfirm(conversationId: string): Promise<void> {
    const user = this.voiceReportService.userEmail() || 'tvms-user';
    const fields = await this.voiceReportService.getConversationFields(conversationId, user);
    if (!fields) return;

    // 记录自动补全日期的字段，并从展示值中剔除内部控制键
    this.timeAutoComplected =
      (fields as any).__timeAutoComplected || this.timeAutoComplected;
    delete (fields as any).__skipped;
    delete (fields as any).__timeAutoComplected;

    this.voiceReportService.setConfirmFields({ ...this.confirmFields(), ...fields });
    this.voiceReportService.setVoiceStep('confirm');
    this.scrollToBottom();
  }

  // ================= 确认卡片 =================

  protected confirmFieldDefs = computed(() =>
    this.reportType() ? getConfirmFieldDefs(this.reportType()!) : []
  );

  /**
   * 引导提示的采集字段文案：严格按 OTI/LP 区分，帮助驾驶员一次性说完对应字段。
   */
  protected voiceHintSub(): string {
    switch (this.reportType()) {
      case 'OTI':
        return this.t('chatDialog.voiceHintOti');
      case 'LP':
        return this.t('chatDialog.voiceHintLp');
      default:
        return this.t('chatDialog.voiceHintDefault');
    }
  }

  protected async onConfirmSubmit(fields: Record<string, unknown>): Promise<void> {
    const type = this.reportType();
    if (!type) return;
    const checkIn = this.quickActionState.checkInState()?.vehicle;
    const profile = this.authService.getCurrentUserProfile();
    // 剥离确认卡片附带的附件（POC 本地预览），单独传入 mock 报告
    const attachments = (fields['attachments'] as VoiceAttachment[] | undefined) ?? [];
    delete fields['attachments'];
    const report = this.mockReportService.create({
      reportType: type,
      fields,
      attachments,
      vehicleId: checkIn?.id || '',
      driver: profile?.displayName || '',
    });
    // 提交成功后，把 report_id 写入该 Dify conversation 的会话变量，
    // 用于"是否已提交"的判断（跨设备/刷新可靠，防止重复提交）
    const user = this.voiceReportService.userEmail() || 'tvms-user';
    await this.voiceReportService.setReportId(
      this.voiceReportService.conversationId,
      user,
      report.id
    );
    // 提交成功后：在对话中追加成功反馈消息（保留，不清空），
    // 并重置流程状态（voiceStep → idle），模拟真实用户行为。
    this.voiceMessages.update(msgs => [
      ...msgs,
      {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: this.t('chatDialog.reportSubmittedMsg', { id: report.id.slice(0, 8) }),
        timestamp: Date.now(),
        status: 'done',
      },
    ]);
    // 保留消息展示，仅重置流程状态；下次新建才会清空
    this.timeAutoComplected = [];
    this.voiceReportService.completeReport();
    this.scrollToBottom();
  }

  protected onConfirmCancel(): void {
    this.voiceReportService.setVoiceStep('chatting');
  }

  // ================= 通用控制 =================

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  protected clear(): void {
    if (this.mode() === 'general') this.generalService.clearMessages();
    else this.voiceReportService.clearMessages();
  }

  protected cancel(): void {
    if (this.mode() === 'general') this.generalService.cancel();
    else this.voiceReportService.cancel();
  }

  protected retry(): void {
    const callbacks = {
      onChunk: () => this.scrollToBottom(),
      onDone: () => this.scrollToBottom(),
      onError: () => this.scrollToBottom(),
    };
    if (this.mode() === 'general') this.generalService.retryLast(callbacks);
    else this.voiceReportService.retryLast(callbacks);
  }

  protected trackById(_: number, item: { id: string }): string {
    return item.id;
  }

  protected get lastErrorMessage() {
    return [...this.messages()].reverse().find(m => m.role === 'assistant' && m.status === 'error');
  }

  /** 是否有文字可发送 */
  protected get hasInputText(): boolean {
    return this.inputText.trim().length > 0;
  }

  /** 更多功能按钮（+ 号）占位，后续扩展（如上传图片/文件等） */
  protected onExtClick(): void {
    // TODO: 扩展功能占位
  }

  private resetTextareaHeight(): void {
    const el = this.textareaRef()?.nativeElement;
    if (el) el.style.height = '';
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = document.querySelector('.chat-dialog__messages');
      if (el) el.scrollTop = el.scrollHeight;
    }, 30);
  }
}
